import React, { useState } from 'react';

const SECTIONS = [
  { key: 'executiveSummary', label: 'Executive Summary', type: 'text' },
  { key: 'campaignGoals', label: 'Campaign Goals', type: 'mixed' },
  { key: 'targetAudience', label: 'Target Audience', type: 'mixed' },
  { key: 'competitiveLandscape', label: 'Competitive Landscape', type: 'text' },
  { key: 'brandPositioning', label: 'Brand Positioning', type: 'mixed' },
  { key: 'channelStrategy', label: 'Channel Strategy', type: 'mixed' },
  { key: 'contentPillars', label: 'Content Pillars', type: 'mixed' },
  { key: 'campaignPhases', label: 'Campaign Phases', type: 'mixed' },
  { key: 'budgetAllocation', label: 'Budget Allocation', type: 'mixed' },
  { key: 'kpisAndMeasurement', label: 'KPIs & Measurement', type: 'mixed' },
  { key: 'riskMitigation', label: 'Risk Mitigation', type: 'mixed' },
];

const PLATFORM_COLORS = {
  instagram: '#E1306C',
  facebook: '#1877F2',
  linkedin: '#0A66C2',
  twitter: '#1DA1F2',
  x: '#000000',
  youtube: '#FF0000',
};

const MarketingStrategyDocument = ({ strategy, isDarkMode, onSectionEdit, onRegenerateSection, onApprove, onDiscard, submitting, regeneratingSection }) => {
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    SECTIONS.forEach(s => { initial[s.key] = true; });
    return initial;
  });
  const [editingSection, setEditingSection] = useState(null);
  const [editValue, setEditValue] = useState('');

  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Recursively format a value (string, array, or object) into readable text.
  // Handles nested arrays-of-objects (e.g. Core Messaging Framework) which
  // plain Array.prototype.join() cannot stringify (it yields "[object Object]").
  const formatInnerValue = (v) => {
    if (Array.isArray(v)) {
      return v
        .map((x) =>
          typeof x === 'object' && x !== null
            ? Object.entries(x)
                .map(([ik, iv]) => `${ik.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: ${formatInnerValue(iv)}`)
                .join('; ')
            : String(x)
        )
        .join(', ');
    }
    if (typeof v === 'object' && v !== null) {
      return Object.entries(v)
        .map(([ik, iv]) => `${ik.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: ${formatInnerValue(iv)}`)
        .join('; ');
    }
    return String(v ?? '');
  };

  // Convert a value to a human-readable editable string
  const valueToEditString = (value) => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      // Array of objects (e.g. campaignGoals): render as labeled blocks
      return value.map((item, i) => {
        if (typeof item === 'object' && item !== null) {
          return Object.entries(item)
            .map(([k, v]) => `${k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: ${formatInnerValue(v)}`)
            .join('\n');
        }
        return String(item);
      }).join('\n\n');
    }
    if (typeof value === 'object' && value !== null) {
      return Object.entries(value)
        .map(([k, v]) => `${k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: ${formatInnerValue(v)}`)
        .join('\n');
    }
    return String(value ?? '');
  };

  const startEdit = (key, value) => {
    setEditingSection(key);
    setEditValue(valueToEditString(value));
  };

  const saveEdit = (key) => {
    // Save as plain string — the display renderer handles it gracefully
    onSectionEdit(key, editValue);
    setEditingSection(null);
  };

  const renderValue = (value, type) => {
  if (value === undefined || value === null) return <span className="italic opacity-50">Not generated</span>;

  // Handle strings
  if (typeof value === 'string') {
    return <p className="text-sm leading-relaxed whitespace-pre-wrap">{value}</p>;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return (
      <div className="space-y-3">
        {value.map((item, i) => {
          if (typeof item === 'string') {
            return <p key={i} className="text-sm leading-relaxed">• {item}</p>;
          }
          if (typeof item === 'object' && item !== null) {
            return (
              <div key={i} className={`pl-3 border-l-2 border-cyan-500/30 space-y-1.5`}>
                {Object.entries(item).map(([k, v]) => (
                  <div key={k}>
                    <span className="text-xs font-bold uppercase tracking-wider opacity-60">
                      {k.replace(/_/g, ' ')}:
                    </span>
                    <div className="text-sm mt-0.5 ml-1">
                      {renderNestedValue(v)}
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          return <p key={i} className="text-sm">{String(item)}</p>;
        })}
      </div>
    );
  }

  // Handle objects
  if (typeof value === 'object') {
    return (
      <div className="space-y-2">
        {Object.entries(value).map(([k, v]) => (
          <div key={k}>
            <span className="text-xs font-bold uppercase tracking-wider opacity-60">
              {k.replace(/_/g, ' ')}:
            </span>
            <div className="text-sm mt-0.5 ml-1">
              {renderNestedValue(v)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <span className="text-sm">{String(value)}</span>;
};

// Helper to render nested values without [object Object]
const renderNestedValue = (v) => {
  if (v === null || v === undefined) return <span className="opacity-50 italic">—</span>;
  if (typeof v === 'string') return <span>{v}</span>;
  if (typeof v === 'number' || typeof v === 'boolean') return <span>{String(v)}</span>;
  if (Array.isArray(v)) {
    return (
      <div className="space-y-1 mt-1">
        {v.map((item, i) => {
          if (typeof item === 'string') return <p key={i} className="text-sm">• {item}</p>;
          if (typeof item === 'object' && item !== null) {
            return (
              <div key={i} className="pl-3 border-l border-cyan-500/20 space-y-1 mt-1">
                {Object.entries(item).map(([ik, iv]) => (
                  <div key={ik}>
                    <span className="text-xs font-semibold uppercase tracking-wide opacity-60">
                      {ik.replace(/_/g, ' ')}:
                    </span>{' '}
                    <span className="text-sm">
                      {Array.isArray(iv)
                        ? iv.map((x, xi) => (
                            <span key={xi} className="block ml-2">• {typeof x === 'object' ? JSON.stringify(x) : String(x)}</span>
                          ))
                        : typeof iv === 'object' && iv !== null
                        ? Object.entries(iv).map(([jk, jv]) => (
                            <span key={jk} className="block ml-2">
                              <span className="font-semibold">{jk}:</span> {String(jv)}
                            </span>
                          ))
                        : String(iv)}
                    </span>
                  </div>
                ))}
              </div>
            );
          }
          return <span key={i} className="text-sm">• {String(item)}</span>;
        })}
      </div>
    );
  }
  if (typeof v === 'object') {
    return (
      <div className="space-y-1 mt-1 pl-2">
        {Object.entries(v).map(([ok, ov]) => (
          <div key={ok}>
            <span className="text-xs font-semibold uppercase tracking-wide opacity-60">
              {ok.replace(/_/g, ' ')}:
            </span>{' '}
            <span className="text-sm">
              {Array.isArray(ov)
                ? ov.join(', ')
                : typeof ov === 'object' && ov !== null
                ? JSON.stringify(ov)
                : String(ov)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(v)}</span>;
};

  const bubbleBg = isDarkMode ? 'bg-gray-800' : 'bg-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const sectionBg = isDarkMode ? 'bg-gray-900/50' : 'bg-white';

  return (
    <div className="flex items-start gap-2 mb-4">
      {/* Bot Avatar */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${isDarkMode
        ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30'
        : 'bg-gradient-to-br from-cyan-100 to-blue-100 border border-cyan-300'
      }`}>
        <svg className="w-4 h-4 text-cyan-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
          <path d="M18 4L18.5 6.5L21 7L18.5 7.5L18 10L17.5 7.5L15 7L17.5 6.5L18 4Z" />
        </svg>
      </div>

      <div className="flex-1 min-w-0 max-w-[90%] md:max-w-[80%]">
        {/* Assistant label */}
        <p className={`text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
          ✨ AI Marketing Strategist — Strategy Document
        </p>

        {/* Main bubble */}
        <div className={`px-4 py-4 rounded-2xl shadow-sm ${bubbleBg} ${isDarkMode ? 'border border-gray-700/50' : ''}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isDarkMode
                ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30'
                : 'bg-gradient-to-br from-cyan-100 to-blue-100 border border-cyan-300'
              }`}>
                <svg className="w-3.5 h-3.5 text-cyan-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
                </svg>
              </div>
              <div>
                <h4 className={`text-sm font-bold ${textPrimary}`}>Marketing Strategy</h4>
                <p className={`text-xs ${textMuted}`}>Review, edit, and approve before generating content</p>
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${isDarkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>
              Draft
            </span>
          </div>

          {/* Channel recommendation pills */}
          {strategy?.channelStrategy && (
            <div className="flex flex-wrap gap-2 mb-4">
              {strategy.channelStrategy.map((ch, i) => (
                <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium border ${isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
                  <span className="font-bold">{ch.channel || ch.platform}</span>
                  <span className={`ml-1.5 ${textMuted}`}>{ch.allocation}</span>
                </span>
              ))}
            </div>
          )}

          {/* Sections */}
          <div className="space-y-2 mb-4">
            {SECTIONS.map(section => {
              const value = strategy?.[section.key];
              const isOpen = openSections[section.key];
              const isEditing = editingSection === section.key;

              return (
                <div key={section.key} className={`rounded-xl border ${borderColor} overflow-hidden`}>
                  {/* Section header */}
                  <button
                    onClick={() => toggleSection(section.key)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors ${sectionBg} hover:opacity-80`}
                  >
                    <span className={`text-sm font-semibold ${textPrimary}`}>{section.label}</span>
                    <div className="flex items-center gap-2">
                      {!isEditing && (
                        <button
                          onClick={(e) => { e.stopPropagation(); startEdit(section.key, value); }}
                          className={`text-xs px-2 py-0.5 rounded transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                        >
                          Edit
                        </button>
                      )}
                      {!isEditing && (
                        <button
                          onClick={(e) => { e.stopPropagation(); if (!submitting) onRegenerateSection(section.key); }}
                          disabled={submitting}
                          className={`text-xs px-2 py-0.5 rounded transition-colors flex items-center gap-1 ${
                            isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {regeneratingSection === section.key ? (
                            <>
                              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Regenerating...
                            </>
                          ) : 'Regenerate'}
                        </button>
                      )}
                      <svg className={`w-4 h-4 ${textSecondary} transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Section body */}
                  {isOpen && (
                    <div className={`px-3 py-3 ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                      {isEditing ? (
                        <div className="space-y-2">
                          <textarea
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            rows={6}
                            className={`w-full px-3 py-2 text-sm rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveEdit(section.key)}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-cyan-500 hover:bg-cyan-600 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingSection(null)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={textPrimary}>
                          {renderValue(value, section.type)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={onApprove}
              disabled={submitting}
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 active:scale-[0.98] hover:shadow-lg hover:shadow-cyan-500/25 hover:brightness-110 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)' }}
            >
              {submitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Approve Strategy → Choose Channels</span>
                </>
              )}
            </button>
            <button
              onClick={onDiscard}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 active:scale-[0.98] ${isDarkMode
                ? 'border border-gray-600 text-gray-300 hover:bg-gray-700/60'
                : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Discard & Restart
            </button>
          </div>

          {/* Branding */}
          <div className="flex items-center mt-2 px-1">
            <span className={`text-[10px] ${textMuted}`}>⚡ Powered by Infinium</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingStrategyDocument;